import { useState, useEffect, Fragment } from 'react';
import { makeStyles } from '@mui/styles';
import {
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  Link,
  MenuItem,
  Select,
  TextField,
  Theme,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { formatValues, snakeToCamelCase } from '#app/utils';
import { createAccountFormSubmitted } from '#app/fullstory-events/events';
import { SubmitHandler, useForm } from 'react-hook-form';
import {
  RawApplicantInformation,
  ApplicantInformation,
} from '#app/@types/ApplicantInformation';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: useTheme().spacing(2),
  },
  label: {
    [theme.breakpoints.down('md')]: {
      fontSize: 24,
      marginTop: 58,
    },
    [theme.breakpoints.up('md')]: {
      fontSize: 32,
      marginTop: 95,
    },
    fontWeight: 700,
    marginBottom: 10,
    textAlign: 'left',
  },
  nameContainer: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'nowrap',
    justifyContent: 'space-between',
  },
  firstName: {
    [theme.breakpoints.down('md')]: {
      paddingRight: 8,
    },
  },
  lastName: {
    [theme.breakpoints.down('md')]: {
      paddingLeft: 8,
    },
  },
  disclaimer: {
    color: '#718096',
    marginBottom: 10,
  },
  formItem: {
    margin: '24px 0px',
  },
  link: {
    fontSize: 16,
    paddingLeft: 6,
    paddingRight: 6,
  },
  button: {
    margin: useTheme().spacing(1),
  },
  buttonText: {
    textTransform: 'capitalize',
    fontWeight: 700,
  },
}));

interface Props {
  defaultValues: RawApplicantInformation;
}

interface EquipmentData {
  name: string;
  val: string;
}

type FormData = {
  companyName: string;
  email: string;
  equipmentType: string;
  firstName: string;
  lastName: string;
  mcNumber: string;
  telephone: string;
  agreeToTerms: boolean;
  assertIdentity: boolean;
};

type RegistrationFormError = {
  msg: string;
  field?: string;
};

function RegistrationForm(props: Props) {
  const { defaultValues } = props;
  const formattedDefaultValues: ApplicantInformation =
    formatValues(defaultValues);
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<FormData>({ defaultValues: formattedDefaultValues });
  const { applicantId, dotNumber } = formattedDefaultValues;

  const classes = useStyles();
  const navigate = useNavigate();
  const [disableButtons, setDisableButtons] = useState(false);
  const [equipment, setEquipment] = useState<EquipmentData[]>([]);

  useEffect(() => {
    const getEquipmentTypes = async () => {
      let body = [];
      try {
        const response = await fetch('/api/registration/equipment-type', {
          headers: {
            'Content-Type': 'application/json',
          },
          method: 'GET',
        });

        if (response.ok) {
          body = await response.json();
        } else {
          console.error('Failed to get equipment type options', response);
        }
      } catch (e) {
        console.error(
          'Something went wrong fetching equipment type options',
          e
        );
      } finally {
        setEquipment(body);
      }
    };
    getEquipmentTypes();
  }, [setEquipment]);

  const onSubmit: SubmitHandler<FormData> = async (data, e) => {
    setDisableButtons(true);

    try {
      const response = await fetch('/api/registration/applicant-details', {
        headers: {
          'Content-Type': 'application/json',
        },
        method: 'POST',
        body: JSON.stringify({
          applicant_id: applicantId,
          first_name: data.firstName,
          last_name: data.lastName,
          company_name: data.companyName,
          telephone: data.telephone.replace(/\D/g, ''),
          chosen_dot_email: data.email,
          mc_number: data.mcNumber,
          agree_to_terms: data.agreeToTerms,
          assert_identity: data.assertIdentity,
          equipment_type: data.equipmentType,
        }),
      });

      const body = await response.json();

      if (!response.ok) {
        if (body.errors) {
          body.errors.forEach((error: RegistrationFormError) => {
            if (error.field) {
              setError(snakeToCamelCase(error.field) as keyof FormData, {
                type: 'custom',
                message: error.msg,
              });
            } else {
              throw new Error(
                `An error has occurred: ${response.status}; ${JSON.stringify(
                  body.errors
                )}`
              );
            }
          });
        } else {
          throw new Error(`An error has occurred: ${response.status}`);
        }
      } else {
        navigate('/registration/wait-for-email', {
          state: { chosenDotEmail: data.email },
        });
      }
    } catch (e) {
      console.error('Failed to post applicant data', e);
    } finally {
      // Let FullStory know the Submit button was clicked
      createAccountFormSubmitted({
        chosenDotEmail: data.email,
        firstName: data.firstName,
        lastName: data.lastName,
        mcNumber: data.mcNumber,
        companyName: data.companyName,
      });
      setDisableButtons(false);
    }
  };

  const handleClick = () => {
    navigate('/registration/start-application');
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Grid container>
        <div className={classes.label}>Create your account</div>
        <div className={classes.disclaimer}>
          Just a heads up, we retrieved this info from the FMCSA. To change your
          DOT #, please go back to the previous page
        </div>
        <TextField
          className={classes.formItem}
          data-testid="input-dotNumber"
          required
          fullWidth
          id="dotNumber"
          label="DOT Number"
          variant="outlined"
          value={dotNumber}
          disabled={true}
          style={{ marginBottom: 24 }}
        />
        <TextField
          data-testid="input-mcNumber"
          error={!!errors.mcNumber?.type}
          helperText={errors.mcNumber?.message}
          fullWidth
          id="mcNumber"
          variant="outlined"
          placeholder="MC Number"
          label="MC Number"
          style={{ marginBottom: 24 }}
          {...register('mcNumber', {
            required: 'Please enter your MC number',
            pattern: {
              value: /^[a-zA-Z0-9]{1,10}$/,
              message: 'Please enter a valid MC number',
            },
          })}
        />
        <TextField
          error={!!errors.email?.type}
          helperText={
            errors.email?.message === 'This email is already registered.' ? (
              <Fragment>
                {errors.email?.message}{' '}
                <Link href="/" style={{ textDecoration: 'underline' }}>
                  Sign in
                </Link>
                {' if you have an account.'}
              </Fragment>
            ) : (
              errors.email?.message
            )
          }
          fullWidth
          data-testid="input-email"
          id="dotEmail"
          variant="outlined"
          placeholder="Email"
          label="Email"
          style={{ marginBottom: 24 }}
          {...register('email', {
            required: 'Please enter your email address',
            pattern: {
              value:
                /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
              message: 'Please enter a valid email address',
            },
          })}
        />
        <TextField
          fullWidth
          id="telephone"
          data-testid="input-telephone"
          error={!!errors.telephone?.type}
          helperText={errors.telephone?.message}
          variant="outlined"
          placeholder="Phone Number"
          label="Phone Number"
          style={{ marginBottom: 24 }}
          {...register('telephone', {
            required: 'Please enter your phone number',
            pattern: {
              value: /^(?:\(\d{3}\)\s\d{3}-\d{4}|\d{10})$/,
              message: 'Please enter a valid US phone number (10 digits)',
            },
          })}
        />
        <TextField
          fullWidth
          id="companyName"
          data-testid="input-companyName"
          error={!!errors.companyName?.type}
          helperText={errors.companyName?.message}
          variant="outlined"
          placeholder="Business Name"
          label="Business Name"
          style={{ marginBottom: 24 }}
          {...register('companyName', {
            required: 'Please enter your company name',
          })}
        />
        <FormControl
          fullWidth
          style={{ marginBottom: 24 }}
          error={!!errors.equipmentType?.type}>
          <InputLabel id="select-equipmentTypeLabel" required>
            Equipment Type
          </InputLabel>
          <Select
            labelId="select-equipmentTypeLabel"
            id="equipmentType"
            variant="outlined"
            data-testid="select-equipmentType"
            defaultValue=""
            label="Equipment Type *"
            {...register('equipmentType', {
              required: 'Please select an equipment type',
            })}>
            {equipment.map(({ name, val }) => (
              <MenuItem key={name} value={name}>
                {val}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>{errors.equipmentType?.message}</FormHelperText>
        </FormControl>
        <Grid item container className={classes.nameContainer}>
          <Grid item xs={6} className={classes.firstName}>
            <TextField
              id="firstName"
              data-testid="input-firstName"
              error={!!errors.firstName?.type}
              helperText={errors.firstName?.message}
              variant="outlined"
              placeholder="First Name"
              label="First Name"
              style={{ marginBottom: 24 }}
              {...register('firstName', {
                required: 'Please enter your first name',
                pattern: {
                  value: /^[a-zA-Z0-9\-.',\s]+$/,
                  message:
                    'We accept letters, numbers, and dash, dot, apostrophe, comma, or space',
                },
              })}
            />
          </Grid>
          <Grid item xs={6} className={classes.lastName}>
            <TextField
              fullWidth
              id="lastName"
              data-testid="input-lastName"
              error={!!errors.lastName?.type}
              helperText={errors.lastName?.message}
              variant="outlined"
              placeholder="Last Name"
              label="Last Name"
              style={{ marginBottom: 24 }}
              {...register('lastName', {
                required: 'Please enter your last name',
                pattern: {
                  value: /^[a-zA-Z0-9\-.',\s]+$/,
                  message:
                    'We accept letters, numbers, and dash, dot, apostrophe, comma, or space',
                },
              })}
            />
          </Grid>
        </Grid>
        <div className={classes.row}>
          <FormControlLabel
            control={
              <Checkbox
                color="primary"
                required
                data-testid="checkbox-agreeToTerms"
                {...register('agreeToTerms', {
                  required: 'Please agree to the Terms of Service',
                })}
              />
            }
            label={
              <div>
                By checking this box, you agree to TrueNorth's
                <Link
                  target="_blank"
                  href="https://www.gettruenorth.com/termsandconditions">
                  <span className={classes.link} data-testid="link-terms">
                    Terms of Service
                  </span>
                </Link>
                and to being contacted, including for marketing, using your
                provided details.
              </div>
            }
          />
        </div>
        <div className={classes.row}>
          <FormControlLabel
            control={
              <Checkbox
                color="primary"
                required
                data-testid="checkbox-assertIdentity"
                {...register('assertIdentity', {
                  required: 'Please certify your identity',
                })}
              />
            }
            label={
              'By accepting, you certify that you are the person whose personal details you are submitting'
            }
          />
        </div>
        <Button
          className={classes.button}
          color="primary"
          data-testid="submit-accountDetails"
          disabled={disableButtons}
          fullWidth
          style={{ height: 56 }}
          type="submit"
          variant="contained">
          <span className={classes.buttonText}>Submit</span>
        </Button>

        <Button
          className={classes.button}
          color="primary"
          data-testid="goBack"
          disabled={disableButtons}
          fullWidth
          onClick={handleClick}
          style={{ height: 56, marginTop: 32 }}
          type="button"
          variant="outlined">
          <span className={classes.buttonText}>Back</span>
        </Button>
      </Grid>
    </form>
  );
}

export default RegistrationForm;
